import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Settings, Upload, Camera, Image, Tag } from 'lucide-react';
import { StockItem } from '../lib/stock';
import EditStockModal from './EditStockModal';
import { ProductPhotoService } from '../services/productPhotoService';

interface ProductDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: StockItem | null;
  onItemUpdated?: () => void;
  productPhoto?: string;
  onPhotoUpdated?: (itemId: string, photoUrl: string | null) => void;
}

export default function ProductDetailsModal({
  isOpen,
  onClose,
  item,
  onItemUpdated,
  productPhoto,
  onPhotoUpdated
}: ProductDetailsModalProps) {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  React.useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
    }
  }, [isOpen]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
    }
  };

  const handleEditModalClose = () => {
    setIsEditModalOpen(false);
    if (onItemUpdated) {
      onItemUpdated();
    }
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !item || !onPhotoUpdated) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB');
      return;
    }

    setIsUploading(true);
    
    try {
      // Upload to Supabase Storage and save to database
      const photoUrl = await ProductPhotoService.uploadAndSavePhoto(item.id, file);
      
      // Update the UI with the new photo URL
      onPhotoUpdated(item.id, photoUrl);
      
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveImage = async () => {
    if (!item || !onPhotoUpdated) return;
    
    try {
      // Delete from database and storage
      await ProductPhotoService.deletePhotoAndFile(item.id);
      
      // Update the UI
      onPhotoUpdated(item.id, null);
      
    } catch (error) {
      console.error('Error removing image:', error);
      alert('Failed to remove image. Please try again.');
    }
  };

  if (!isOpen && !isAnimating) return null;
  if (!item) return null;

  const modalContent = (
        <div
      className={`fixed inset-0 bg-black transition-opacity duration-300 ${
        isOpen ? 'bg-opacity-50' : 'bg-opacity-0'
      } z-[100010] flex items-center justify-center p-0 sm:p-4`}
      onClick={onClose}
      style={{
        transform: 'translateZ(0)',
        WebkitTransform: 'translateZ(0)',
        isolation: 'isolate',
        touchAction: 'manipulation',
        WebkitTouchCallout: 'none',
        WebkitUserSelect: 'none',
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
        paddingLeft: 'env(safe-area-inset-left)',
        paddingRight: 'env(safe-area-inset-right)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
      }}
    >
      <div
        className={`backdrop-blur-xl bg-white/90 transform transition-all duration-300 ease-out shadow-2xl w-full h-full sm:h-auto sm:max-h-[90vh] sm:max-w-2xl sm:rounded-2xl overflow-hidden ${
          isOpen ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
        }`}
        style={{
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)'
        }}
        onTransitionEnd={handleTransitionEnd}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col">
                    {/* Header - Mobile Optimized */}
          <div className="relative bg-white border-b border-gray-200 sm:rounded-t-2xl">
            <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4">
              <div className="flex-1 min-w-0">
                <h2 className="text-base sm:text-lg font-bold text-gray-900 truncate">
                  {item.name}
                </h2>
                {item.category?.name && (
                  <div className="flex items-center gap-1 mt-1">
                    <Tag className="h-3 w-3 text-purple-600" />
                    <span className="text-xs text-purple-700 font-medium">{item.category.name}</span>
                  </div>
                )}
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setIsEditModalOpen(true)}
                  className="flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-800 transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 touch-manipulation"
                >
                  <Settings className="h-4 w-4 sm:h-5 sm:w-5" />
                </button>
                <button
                  onClick={onClose}
                  className="flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-800 transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 touch-manipulation"
                >
                  <X className="h-4 w-4 sm:h-5 sm:w-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Content Area - Mobile Optimized */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-6" style={{ WebkitOverflowScrolling: 'touch' }}>
            <div className="space-y-4 sm:space-y-6">
                            {/* Stock Information */}
              <div className="backdrop-blur-xl bg-white/60 rounded-2xl p-4 sm:p-6 shadow-lg border border-blue-200/30"
                   style={{ backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}>
                <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-3 sm:mb-4">Stock Information</h3>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                  {/* Total Stock */}
                  <div className={`bg-white/50 rounded-xl p-4 border ${
                    item.quantity <= item.reorder_point 
                      ? 'border-red-200/50 bg-red-50/30' 
                      : 'border-gray-200/50'
                  }`}>
                    <div className="text-sm font-medium text-gray-600 mb-1">Total in Stock</div>
                    <div className={`text-2xl font-bold ${
                      item.quantity <= item.reorder_point 
                        ? 'text-red-600' 
                        : 'text-gray-900'
                    }`}>
                      {item.quantity} {item.unit}
                    </div>
                  </div>
                  
                  {/* Reorder Point */}
                  <div className="bg-white/50 rounded-xl p-4 border border-gray-200/50">
                    <div className="text-sm font-medium text-gray-600 mb-1">Reorder Point</div>
                    <div className="text-2xl font-bold text-gray-900">
                      {item.reorder_point} {item.unit}
                    </div>
                  </div>

                  {/* Max Quantity */}
                  <div className="bg-white/50 rounded-xl p-4 border border-gray-200/50">
                    <div className="text-sm font-medium text-gray-600 mb-1">Max Capacity</div>
                    <div className="text-2xl font-bold text-gray-900">
                      {item.max_quantity} {item.unit}
                    </div>
                  </div>
                </div>

                {/* Stock Status */}
                <div className="mt-4">
                  {item.quantity <= item.reorder_point ? (
                    <div className="flex items-center px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
                      <div className="w-2 h-2 bg-red-500 rounded-full mr-2"></div>
                      <span className="text-sm font-medium text-red-700">Low Stock - Reorder Needed</span>
                    </div>
                  ) : (
                    <div className="flex items-center px-3 py-2 bg-green-50 border border-green-200 rounded-lg">
                      <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                      <span className="text-sm font-medium text-green-700">Stock Level Good</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Product Photo */}
              <div className="backdrop-blur-xl bg-white/60 rounded-2xl p-6 shadow-lg border border-blue-200/30"
                   style={{ backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}>
                <h3 className="text-lg font-bold text-gray-900 mb-4">Product Photo</h3>
                
                {productPhoto ? (
                  <div className="space-y-4">
                    {/* Image Preview */}
                    <div className="relative">
                      <img 
                        src={productPhoto} 
                        alt={item.name}
                        className="w-full max-h-80 object-contain rounded-xl border border-gray-200 bg-gray-50"
                        style={{ 
                          minHeight: '200px',
                          imageRendering: 'high-quality'
                        }}
                        loading="lazy"
                      />
                      <button
                        onClick={handleRemoveImage}
                        className="absolute top-2 right-2 w-8 h-8 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center transition-colors duration-200 shadow-lg"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                    
                    {/* Replace Photo Button */}
                    <label className="block">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                        disabled={isUploading}
                      />
                      <div className="w-full flex items-center justify-center px-4 py-2 border-2 border-dashed border-gray-300 rounded-xl text-sm text-gray-600 hover:text-gray-900 hover:border-gray-400 cursor-pointer transition-colors duration-200">
                        <Upload className="h-4 w-4 mr-2" />
                        Replace Photo
                      </div>
                    </label>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Upload Area */}
                    <label className="block">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                        disabled={isUploading}
                      />
                      <div className="w-full h-48 flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-xl text-gray-600 hover:text-gray-900 hover:border-gray-400 cursor-pointer transition-colors duration-200">
                        {isUploading ? (
                          <>
                            <div className="w-8 h-8 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin mb-2"></div>
                            <span className="text-sm font-medium">Uploading...</span>
                          </>
                        ) : (
                          <>
                            <Image className="h-12 w-12 mb-2" />
                            <span className="text-sm font-medium mb-1">Upload Product Photo</span>
                            <span className="text-xs text-gray-500">Click to select or drag and drop</span>
                            <span className="text-xs text-gray-400 mt-1">PNG, JPG up to 5MB</span>
                          </>
                        )}
                      </div>
                    </label>
                  </div>
                )}
              </div>

              {/* Quick Actions */}
              <div className="backdrop-blur-xl bg-white/60 rounded-2xl p-6 shadow-lg border border-blue-200/30"
                   style={{ backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)' }}>
                <h3 className="text-lg font-bold text-gray-900 mb-4">Quick Actions</h3>
                
                <button
                  onClick={() => setIsEditModalOpen(true)}
                  className="inline-flex items-center px-4 py-2 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-xl transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 shadow-lg shadow-blue-500/20 touch-manipulation"
                >
                  <Settings className="h-4 w-4 mr-2" />
                  Edit Product Settings
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {createPortal(modalContent, document.body)}
      
      <EditStockModal
        isOpen={isEditModalOpen}
        onClose={handleEditModalClose}
        item={item}
        onItemUpdated={onItemUpdated}
      />
    </>
  );
}
